/**
 * Convert EditorJS JSON to HTML
 * Shared utility function for converting Editor.js JSON format to HTML
 * Compatible with CKEditor and email templates
 */
export const convertEditorJSToHTML = (jsonString) => {
  if (!jsonString) return "";
  
  // Check if it's already HTML (starts with < or doesn't look like JSON)
  const trimmed = jsonString.trim();
  if (trimmed.startsWith('<') || (!trimmed.startsWith('{') && !trimmed.startsWith('['))) {
    // Already HTML, return as-is
    return jsonString;
  }
  
  try {
    const data = JSON.parse(jsonString);
    if (!data || !data.blocks) return jsonString; // Return as-is if not EditorJS format
    
    // Helper to get alignment style
    const getAlignmentStyle = (block) => {
      const alignment = block.tunes?.alignmentTune?.alignment || 'left';
      return alignment !== 'left' ? `text-align: ${alignment};` : '';
    };
    
    // Helper to build style attribute
    const buildStyleAttr = (styles) => {
      return styles ? ` style="${styles}"` : '';
    };
    
    return data.blocks
      .map((block) => {
        const alignStyle = getAlignmentStyle(block);
        
        switch (block.type) {
          case "header": {
            const fontSizes = { 1: '2em', 2: '1.5em', 3: '1.17em', 4: '1em', 5: '0.83em', 6: '0.67em' };
            const fontSize = fontSizes[block.data.level] || '1em';
            const hStyle = `margin: 1em 0 0.5em; font-weight: bold; font-size: ${fontSize};${alignStyle ? ' ' + alignStyle : ''}`;
            return `<h${block.data.level} style="${hStyle}">${block.data.text}</h${block.data.level}>`;
          }
          case "paragraph": {
            const pStyle = `margin: 0.5em 0;${alignStyle ? ' ' + alignStyle : ''}`;
            return `<p style="${pStyle}">${block.data.text}</p>`;
          }
          case "list": {
            const tag = block.data.style === "ordered" ? "ol" : "ul";
            const items = block.data.items
              .map((item) => {
                // Handle both string items and object items (newer EditorJS versions)
                const content = typeof item === 'string' ? item : (item.content || item.text || '');
                return `<li style="margin: 0.25em 0;">${content}</li>`;
              })
              .join("");
            return `<${tag} style="margin: 0.5em 0; padding-left: 1.5em;">${items}</${tag}>`;
          }
          case "table": {
            const withHeadings = block.data.withHeadings || false;
            const rows = block.data.content
              .map((row, rowIndex) => {
                const cells = row.map((cell) => {
                  const cellTag = (withHeadings && rowIndex === 0) ? 'th' : 'td';
                  let cellStyle = "border: 1px solid #ccc; padding: 8px;";
                  if (withHeadings && rowIndex === 0) {
                    cellStyle += " background-color: #f5f5f5; font-weight: bold;";
                  }
                  return `<${cellTag} style="${cellStyle}">${cell}</${cellTag}>`;
                }).join("");
                return `<tr>${cells}</tr>`;
              })
              .join("");
            return `<table style="border-collapse: collapse; width: 100%; margin: 1em 0;"><tbody>${rows}</tbody></table>`;
          }
          case "linkTool":
            return `<a href="${block.data.link}" target="_blank" style="color: #1BACE4; text-decoration: underline;">${block.data.meta?.title || block.data.link}</a>`;
          case "image": {
            const imgCaption = block.data.caption 
              ? `<figcaption style="font-size: 0.9em; color: #666; margin-top: 0.5em; font-style: italic; text-align: center;">${block.data.caption}</figcaption>` 
              : '';
            // Support both old format (file.url) and new format (url directly)
            const imgUrl = block.data.url || block.data.file?.url || '';
            const imgAlignment = block.data.alignment || 'center';
            return `<figure style="margin: 1em 0; text-align: ${imgAlignment};"><img src="${imgUrl}" alt="${block.data.caption || ''}" style="max-width: 100%; height: auto;" />${imgCaption}</figure>`;
          }
          case "imageGallery": {
            const galleryImages = block.data.images || [];
            const galleryLayout = block.data.layout === 'auto' 
              ? Math.min(galleryImages.length || 1, 4) 
              : parseInt(block.data.layout);
            const galleryGap = block.data.gap || '10px';
            const galleryImagesHtml = galleryImages.map(img => {
              const galCaption = img.caption 
                ? `<figcaption style="font-size: 0.9em; color: #666; margin-top: 0.5em; font-style: italic; text-align: center;">${img.caption}</figcaption>` 
                : '';
              return `<figure style="margin: 0;"><img src="${img.url}" alt="${img.caption || ''}" style="width: 100%; height: auto; display: block;" />${galCaption}</figure>`;
            }).join('');
            return `<div style="display: grid; grid-template-columns: repeat(${galleryLayout}, 1fr); gap: ${galleryGap}; margin: 1em 0;">${galleryImagesHtml}</div>`;
          }
          case "delimiter":
            return `<hr style="border: none; border-top: 2px solid #e0e0e0; margin: 2em 0;" />`;
          case "raw":
            return block.data.html || "";
          case "quote": {
            const quoteCaption = block.data.caption 
              ? `<cite style="display: block; margin-top: 0.5em; font-size: 0.9em; color: #888;">— ${block.data.caption}</cite>` 
              : '';
            const quoteStyles = `border-left: 3px solid #1BACE4; padding-left: 15px; margin: 1em 0; font-style: italic; color: #555;${alignStyle ? ' ' + alignStyle : ''}`;
            return `<blockquote style="${quoteStyles}"><p style="margin: 0;">${block.data.text}</p>${quoteCaption}</blockquote>`;
          }
          case "code": {
            const escapedCode = block.data.code
              .replace(/&/g, '&amp;')
              .replace(/</g, '&lt;')
              .replace(/>/g, '&gt;');
            return `<pre style="background-color: #f4f4f4; padding: 1em; border-radius: 4px; overflow-x: auto; margin: 1em 0;"><code style="font-family: monospace; font-size: 0.9em;">${escapedCode}</code></pre>`;
          }
          case "raw":
            return block.data.html || "";
          default:
            return `<p>${block.data?.text || ""}</p>`;
        }
      })
      .join("");
  } catch (e) {
    // If not valid JSON, return as-is (might be HTML already)
    return jsonString;
  }
};

export default convertEditorJSToHTML;
